import React, { useState, useEffect, useCallback } from 'react';
import './Repositorio.css';

const API = '/api/repositorio';

function formatBytes(bytes) {
    bytes = parseInt(bytes) || 0;
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function timeAgo(date) {
    const now = new Date();
    const d = new Date(date);
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Agora';
    if (diff < 3600) return `${Math.floor(diff/60)} min`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    if (diff < 604800) return `${Math.floor(diff/86400)}d`;
    return d.toLocaleDateString('pt-BR');
}

const extIcons = {
    pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
    jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
    dwg: '📐', dxf: '📐', zip: '📦', rar: '📦',
    xml: '📋', txt: '📃', csv: '📊', pptx: '📊', ppt: '📊'
};

function Repositorio() {
    const [pastas, setPastas] = useState([]);
    const [stats, setStats] = useState({ total_arquivos: 0, total_alertas: 0, armazenamento_usado: 0, por_extensao: [] });
    const [vencendo, setVencendo] = useState([]);
    const [atividade, setAtividade] = useState([]);
    const [pastaAtual, setPastaAtual] = useState(null);
    const [subpastas, setSubpastas] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [uploadModal, setUploadModal] = useState(false);
    const [uploadPastaId, setUploadPastaId] = useState('');
    const [uploadDesc, setUploadDesc] = useState('');
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [validadeModal, setValidadeModal] = useState(null);
    const [novaPastaModal, setNovaPastaModal] = useState(false);
    const [novaPastaNome, setNovaPastaNome] = useState('');
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [notification, setNotification] = useState(null);

    const notify = (msg, type='success') => {
        setNotification({ msg, type });
        setTimeout(() => setNotification(null), 4000);
    };

    const carregarDados = useCallback(async () => {
        try {
            setLoading(true);
            const [pRes, sRes, vRes, aRes] = await Promise.all([
                fetch(`${API}/pastas`).then(r => r.json()),
                fetch(`${API}/stats`).then(r => r.json()),
                fetch(`${API}/vencendo`).then(r => r.json()),
                fetch(`${API}/atividade?limit=8`).then(r => r.json())
            ]);
            if (pRes.success) setPastas(pRes.data);
            if (sRes.success) setStats(sRes.data);
            if (vRes.success) setVencendo(vRes.data);
            if (aRes.success) setAtividade(aRes.data);
        } catch (err) {
            console.error('Erro ao carregar repositório:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { carregarDados(); }, [carregarDados]);

    const abrirPasta = async (pasta) => {
        try {
            const [subRes, docRes] = await Promise.all([
                fetch(`${API}/pastas/${pasta.id}/subpastas`).then(r => r.json()),
                fetch(`${API}/pastas/${pasta.id}/documentos`).then(r => r.json())
            ]);
            setPastaAtual(pasta);
            if (subRes.success) setSubpastas(subRes.data);
            if (docRes.success) setDocumentos(docRes.data);
            setSearchResults(null);
            
            // Build breadcrumb
            if (!pasta.pasta_pai_id) {
                setBreadcrumb([pasta]);
            } else {
                setBreadcrumb(prev => [...prev, pasta]);
            }
        } catch (err) {
            console.error('Erro ao abrir pasta:', err);
        }
    };

    const voltarHome = () => {
        setPastaAtual(null);
        setSubpastas([]);
        setDocumentos([]);
        setBreadcrumb([]);
        setSearchResults(null);
        carregarDados();
    };

    const voltarBreadcrumb = (index) => {
        if (index === -1) {
            voltarHome();
        } else {
            const pasta = breadcrumb[index];
            setBreadcrumb(breadcrumb.slice(0, index));
            abrirPasta(pasta);
        }
    };

    const buscar = async (q) => {
        setSearch(q);
        if (q.length < 2) { setSearchResults(null); return; }
        try {
            const res = await fetch(`${API}/busca?q=${encodeURIComponent(q)}`).then(r => r.json());
            if (res.success) setSearchResults(res.data);
        } catch (err) { console.error(err); }
    };

    const handleUpload = async () => {
        if (!uploadFiles.length || !uploadPastaId) { notify('Selecione arquivos e pasta', 'error'); return; }
        setUploading(true);
        try {
            const formData = new FormData();
            for (const f of uploadFiles) formData.append('arquivos', f);
            formData.append('pasta_id', uploadPastaId);
            formData.append('descricao', uploadDesc);
            
            const res = await fetch(`${API}/documentos/upload`, { method: 'POST', body: formData }).then(r => r.json());
            if (res.success) {
                notify(`✅ ${res.data.length} arquivo(s) enviado(s)!`);
                setUploadModal(false);
                setUploadFiles([]);
                setUploadDesc('');
                if (pastaAtual) abrirPasta(pastaAtual);
                carregarDados();
            } else {
                notify(res.error || 'Erro no upload', 'error');
            }
        } catch (err) {
            notify('Erro no upload: ' + err.message, 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (docId, nome) => {
        if (!window.confirm(`Excluir "${nome}"?`)) return;
        try {
            const res = await fetch(`${API}/documentos/${docId}`, { method: 'DELETE' }).then(r => r.json());
            if (res.success) {
                notify('🗑️ Documento excluído');
                if (pastaAtual) abrirPasta(pastaAtual);
                carregarDados();
            }
        } catch (err) { notify('Erro ao excluir', 'error'); }
    };

    const handleSalvarValidade = async () => {
        if (!validadeModal) return;
        try {
            const res = await fetch(`${API}/documentos/${validadeModal.id}/validade`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(validadeModal)
            }).then(r => r.json());
            if (res.success) {
                notify('✅ Validade salva!');
                setValidadeModal(null);
                if (pastaAtual) abrirPasta(pastaAtual);
                carregarDados();
            }
        } catch (err) { notify('Erro ao salvar validade', 'error'); }
    };

    const criarSubpasta = async () => {
        if (!novaPastaNome.trim()) return;
        try {
            const res = await fetch(`${API}/pastas`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nome: novaPastaNome, pasta_pai_id: pastaAtual?.id || null })
            }).then(r => r.json());
            if (res.success) {
                notify('✅ Pasta criada!');
                setNovaPastaModal(false);
                setNovaPastaNome('');
                if (pastaAtual) abrirPasta(pastaAtual);
                else carregarDados();
            }
        } catch (err) { notify('Erro ao criar pasta', 'error'); }
    };

    const sevColor = s => s === 'critical' ? '#E74C3C' : s === 'warning' ? '#F39C12' : '#3498DB';

    if (loading) return <div className="repo-loading"><div className="repo-spinner"></div><p>Carregando Repositório...</p></div>;

    // ═══ TELA INTERNA DA PASTA ═══
    if (pastaAtual) {
        return (
            <div className="repo-container">
                {notification && <div className={`repo-notif repo-notif-${notification.type}`}>{notification.msg}</div>}
                
                <div className="repo-inner-header">
                    <div className="repo-breadcrumb">
                        <span className="repo-bc-item" onClick={voltarHome}>📁 Repositório</span>
                        {breadcrumb.map((bc, i) => (
                            <React.Fragment key={bc.id}>
                                <span className="repo-bc-sep">›</span>
                                <span className={`repo-bc-item ${i === breadcrumb.length - 1 ? 'repo-bc-active' : ''}`} 
                                      onClick={() => i < breadcrumb.length - 1 ? voltarBreadcrumb(i) : null}>
                                    {bc.icone} {bc.nome}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="repo-inner-actions">
                        <button className="repo-btn-secondary" onClick={() => setNovaPastaModal(true)}>📁 Nova Subpasta</button>
                        <button className="repo-btn-primary" onClick={() => { setUploadModal(true); setUploadPastaId(pastaAtual.id); }}>+ Upload</button>
                    </div>
                </div>

                {/* Subpastas */}
                {subpastas.length > 0 && (
                    <div className="repo-section">
                        <h3 className="repo-section-title">Subpastas</h3>
                        <div className="repo-subfolders-grid">
                            {subpastas.map(sp => (
                                <div key={sp.id} className="repo-subfolder-card" onClick={() => abrirPasta(sp)}>
                                    <span className="repo-sf-icon">{sp.icone}</span>
                                    <div>
                                        <div className="repo-sf-name">{sp.nome}</div>
                                        <div className="repo-sf-meta">{sp.total_arquivos || 0} arquivos • {formatBytes(sp.tamanho_total)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Documentos */}
                <div className="repo-section">
                    <div className="repo-section-header">
                        <h3 className="repo-section-title">Documentos ({documentos.length})</h3>
                    </div>
                    {documentos.length === 0 ? (
                        <div className="repo-empty">
                            <span style={{fontSize:40}}>📂</span>
                            <p>Nenhum documento nesta pasta</p>
                            <button className="repo-btn-primary" onClick={() => { setUploadModal(true); setUploadPastaId(pastaAtual.id); }}>Fazer Upload</button>
                        </div>
                    ) : (
                        <div className="repo-docs-table">
                            <div className="repo-docs-header">
                                <span className="repo-dh-name">Nome</span>
                                <span className="repo-dh-size">Tamanho</span>
                                <span className="repo-dh-date">Data</span>
                                <span className="repo-dh-validity">Validade</span>
                                <span className="repo-dh-actions">Ações</span>
                            </div>
                            {documentos.map(doc => (
                                <div key={doc.id} className="repo-doc-row">
                                    <span className="repo-doc-name">
                                        <span className="repo-doc-icon">{extIcons[doc.extensao] || '📄'}</span>
                                        <a href={`${API}/documentos/${doc.id}/download`} target="_blank" rel="noreferrer">{doc.nome}</a>
                                    </span>
                                    <span className="repo-doc-size">{formatBytes(doc.tamanho)}</span>
                                    <span className="repo-doc-date">{timeAgo(doc.criado_em)}</span>
                                    <span className="repo-doc-validity">
                                        {doc.tem_validade && doc.data_validade ? (
                                            <span className={`repo-validity-badge repo-sev-${
                                                Math.ceil((new Date(doc.data_validade) - new Date()) / 86400000) <= 7 ? 'critical' :
                                                Math.ceil((new Date(doc.data_validade) - new Date()) / 86400000) <= 15 ? 'warning' : 'info'
                                            }`}>
                                                {new Date(doc.data_validade).toLocaleDateString('pt-BR')}
                                            </span>
                                        ) : (
                                            <span className="repo-no-validity" onClick={() => setValidadeModal({
                                                id: doc.id, tem_validade: true, data_emissao: '', data_validade: '',
                                                orgao_emissor: '', tipo_documento: ''
                                            })}>+ Validade</span>
                                        )}
                                    </span>
                                    <span className="repo-doc-actions">
                                        <button title="Validade" onClick={() => setValidadeModal({
                                            id: doc.id, tem_validade: doc.tem_validade || true,
                                            data_emissao: doc.data_emissao || '', data_validade: doc.data_validade || '',
                                            orgao_emissor: doc.orgao_emissor || '', tipo_documento: doc.tipo_documento || ''
                                        })}>📅</button>
                                        <a href={`${API}/documentos/${doc.id}/download`} title="Download">⬇️</a>
                                        <button title="Excluir" onClick={() => handleDelete(doc.id, doc.nome_original)}>🗑️</button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modal Validade */}
                {validadeModal && (
                    <div className="repo-modal-overlay" onClick={() => setValidadeModal(null)}>
                        <div className="repo-modal" onClick={e => e.stopPropagation()}>
                            <div className="repo-modal-header">
                                <h3>📅 Configurar Validade</h3>
                                <button onClick={() => setValidadeModal(null)}>✕</button>
                            </div>
                            <div className="repo-modal-body">
                                <label>Tipo do Documento</label>
                                <input type="text" placeholder="Ex: CND Federal, Alvará..." value={validadeModal.tipo_documento}
                                    onChange={e => setValidadeModal({...validadeModal, tipo_documento: e.target.value})} />
                                <label>Órgão Emissor</label>
                                <input type="text" placeholder="Ex: Receita Federal..." value={validadeModal.orgao_emissor}
                                    onChange={e => setValidadeModal({...validadeModal, orgao_emissor: e.target.value})} />
                                <label>Data de Emissão</label>
                                <input type="date" value={validadeModal.data_emissao ? validadeModal.data_emissao.substring(0,10) : ''}
                                    onChange={e => setValidadeModal({...validadeModal, data_emissao: e.target.value})} />
                                <label>Data de Validade</label>
                                <input type="date" value={validadeModal.data_validade ? validadeModal.data_validade.substring(0,10) : ''}
                                    onChange={e => setValidadeModal({...validadeModal, data_validade: e.target.value})} />
                                <button className="repo-btn-primary repo-btn-full" onClick={handleSalvarValidade}>Salvar Validade</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal Nova Subpasta */}
                {novaPastaModal && (
                    <div className="repo-modal-overlay" onClick={() => setNovaPastaModal(false)}>
                        <div className="repo-modal" onClick={e => e.stopPropagation()}>
                            <div className="repo-modal-header">
                                <h3>📁 Nova Subpasta</h3>
                                <button onClick={() => setNovaPastaModal(false)}>✕</button>
                            </div>
                            <div className="repo-modal-body">
                                <label>Nome da Pasta</label>
                                <input type="text" placeholder="Ex: Janeiro 2026..." value={novaPastaNome}
                                    onChange={e => setNovaPastaNome(e.target.value)} autoFocus />
                                <button className="repo-btn-primary repo-btn-full" onClick={criarSubpasta}>Criar Pasta</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal Upload */}
                {uploadModal && renderUploadModal()}
            </div>
        );
    }

    // ═══ FUNÇÃO RENDER MODAL UPLOAD (reutilizada) ═══
    function renderUploadModal() {
        return (
            <div className="repo-modal-overlay" onClick={() => setUploadModal(false)}>
                <div className="repo-modal repo-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="repo-modal-header">
                        <h3>📤 Upload de Documentos</h3>
                        <button onClick={() => setUploadModal(false)}>✕</button>
                    </div>
                    <div className="repo-modal-body">
                        <div className={`repo-dropzone ${uploadFiles.length ? 'repo-dropzone-has' : ''}`}
                            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('repo-dropzone-active'); }}
                            onDragLeave={e => e.currentTarget.classList.remove('repo-dropzone-active')}
                            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('repo-dropzone-active'); setUploadFiles([...uploadFiles, ...Array.from(e.dataTransfer.files)]); }}
                            onClick={() => document.getElementById('repo-file-input').click()}>
                            <input id="repo-file-input" type="file" multiple hidden onChange={e => setUploadFiles([...uploadFiles, ...Array.from(e.target.files)])} />
                            {uploadFiles.length === 0 ? (
                                <>
                                    <span style={{fontSize:36}}>📁</span>
                                    <p><strong>Arraste arquivos aqui</strong> ou clique para selecionar</p>
                                    <small>PDF, DOCX, XLSX, IMG, DWG — Máx. 50MB</small>
                                </>
                            ) : (
                                <div className="repo-files-list">
                                    {uploadFiles.map((f, i) => (
                                        <div key={i} className="repo-file-item">
                                            <span>{extIcons[f.name.split('.').pop().toLowerCase()] || '📄'} {f.name}</span>
                                            <span className="repo-file-size">{formatBytes(f.size)}</span>
                                            <button onClick={e => { e.stopPropagation(); setUploadFiles(uploadFiles.filter((_, j) => j !== i)); }}>✕</button>
                                        </div>
                                    ))}
                                    <small>Clique para adicionar mais</small>
                                </div>
                            )}
                        </div>
                        <label>Pasta de destino</label>
                        <select value={uploadPastaId} onChange={e => setUploadPastaId(e.target.value)}>
                            <option value="">Selecionar pasta...</option>
                            {pastas.map(p => (
                                <option key={p.id} value={p.id}>{p.icone} {p.nome}</option>
                            ))}
                        </select>
                        <label>Descrição (opcional)</label>
                        <input type="text" placeholder="Ex: CND Federal renovada fev/2026" value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} />
                        <button className="repo-btn-primary repo-btn-full" onClick={handleUpload} disabled={uploading}>
                            {uploading ? '⏳ Enviando...' : `Enviar ${uploadFiles.length} arquivo(s)`}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ═══ TELA PRINCIPAL (HOME DO REPOSITÓRIO) ═══
    return (
        <div className="repo-container">
            {notification && <div className={`repo-notif repo-notif-${notification.type}`}>{notification.msg}</div>}
            
            {/* Título */}
            <div className="repo-page-header">
                <div>
                    <h1 className="repo-title">📁 Repositório de Documentos</h1>
                    <p className="repo-subtitle">Gestão centralizada com monitoramento inteligente de validade</p>
                </div>
                <div className="repo-header-actions">
                    <div className="repo-view-toggle">
                        <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>⊞</button>
                        <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>☰</button>
                    </div>
                    <button className="repo-btn-primary" onClick={() => setUploadModal(true)}>+ Upload</button>
                </div>
            </div>

            {/* Stats */}
            <div className="repo-stats">
                {[
                    { label: 'Total de Arquivos', val: stats.total_arquivos, icon: '📄', color: '#2E86C1' },
                    { label: 'Docs com Alerta', val: stats.total_alertas, icon: '⚠️', color: '#E74C3C' },
                    { label: 'Armazenamento', val: formatBytes(stats.armazenamento_usado), icon: '💾', color: '#27AE60' },
                    { label: 'Pastas', val: pastas.length, icon: '📁', color: '#8E44AD' }
                ].map((s, i) => (
                    <div key={i} className="repo-stat-card" style={{ borderLeftColor: s.color }}>
                        <span className="repo-stat-icon">{s.icon}</span>
                        <div>
                            <div className="repo-stat-label">{s.label}</div>
                            <div className="repo-stat-value">{s.val}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Search */}
            <div className="repo-search-bar">
                <span>🔍</span>
                <input type="text" placeholder="Buscar documentos, pastas ou conteúdo..." value={search} onChange={e => buscar(e.target.value)} />
                {search && <button onClick={() => { setSearch(''); setSearchResults(null); }}>Limpar</button>}
            </div>

            {/* Search Results */}
            {searchResults ? (
                <div className="repo-search-results">
                    <h3>Resultados para "{search}"</h3>
                    {searchResults.pastas.length > 0 && (
                        <div className="repo-sr-section">
                            <h4>📁 Pastas ({searchResults.pastas.length})</h4>
                            {searchResults.pastas.map(p => (
                                <div key={p.id} className="repo-sr-item" onClick={() => abrirPasta(p)}>
                                    {p.icone} {p.nome} {p.pasta_pai_nome && <small>em {p.pasta_pai_nome}</small>}
                                </div>
                            ))}
                        </div>
                    )}
                    {searchResults.documentos.length > 0 && (
                        <div className="repo-sr-section">
                            <h4>📄 Documentos ({searchResults.documentos.length})</h4>
                            {searchResults.documentos.map(d => (
                                <div key={d.id} className="repo-sr-item">
                                    {extIcons[d.extensao] || '📄'} <a href={`${API}/documentos/${d.id}/download`}>{d.nome}</a>
                                    <small>em {d.pasta_nome}</small>
                                </div>
                            ))}
                        </div>
                    )}
                    {searchResults.pastas.length === 0 && searchResults.documentos.length === 0 && (
                        <p className="repo-empty-msg">Nenhum resultado encontrado</p>
                    )}
                </div>
            ) : (
                /* Main Grid */
                <div className="repo-main-grid">
                    {/* Left: Folders */}
                    <div className="repo-folders-area">
                        <div className="repo-section-header">
                            <h2>Pastas Principais</h2>
                            <span>{pastas.length} pastas</span>
                        </div>
                        <div className={`repo-folders-grid ${viewMode === 'list' ? 'repo-list-mode' : ''}`}>
                            {pastas.map(p => (
                                <div key={p.id} className="repo-folder-card" onClick={() => abrirPasta(p)}>
                                    <div className="repo-fc-head">
                                        <div className="repo-fc-icon" style={{ background: `${p.cor}15` }}>{p.icone}</div>
                                        <div className="repo-fc-info">
                                            <div className="repo-fc-name">
                                                {p.nome}
                                                {parseInt(p.alertas) > 0 && <span className="repo-fc-badge">{p.alertas}</span>}
                                            </div>
                                            {viewMode === 'grid' && <div className="repo-fc-desc">{p.descricao}</div>}
                                        </div>
                                    </div>
                                    <div className="repo-fc-meta">
                                        <div><small>Arquivos</small><strong>{p.total_arquivos || 0}</strong></div>
                                        <div><small>Subpastas</small><strong>{p.total_subpastas || 0}</strong></div>
                                        <div><small>Tamanho</small><strong>{formatBytes(p.tamanho_total)}</strong></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Sidebar */}
                    <div className="repo-sidebar">
                        {/* Vencendo */}
                        <div className="repo-sidebar-card">
                            <div className="repo-sc-header repo-sc-red">
                                <span>⚠️ Documentos Vencendo</span>
                                <span className="repo-sc-count">{vencendo.length}</span>
                            </div>
                            <div className="repo-sc-body">
                                {vencendo.length === 0 ? (
                                    <p className="repo-empty-msg">Nenhum documento vencendo</p>
                                ) : vencendo.slice(0, 6).map(v => (
                                    <div key={v.id} className="repo-exp-item">
                                        <div className="repo-exp-dot" style={{ background: sevColor(v.severidade) }}></div>
                                        <div className="repo-exp-info">
                                            <div className="repo-exp-name">{v.nome_original}</div>
                                            <div className="repo-exp-meta">
                                                <span>📁 {v.pasta_nome}</span>
                                                <span style={{ color: sevColor(v.severidade), fontWeight: 700 }}>{v.dias_restantes}d</span>
                                            </div>
                                            <div className="repo-exp-date">Vence {new Date(v.data_validade).toLocaleDateString('pt-BR')}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Atividade Recente */}
                        <div className="repo-sidebar-card">
                            <div className="repo-sc-header repo-sc-dark">
                                <span>🕐 Atividade Recente</span>
                            </div>
                            <div className="repo-sc-body">
                                {atividade.length === 0 ? (
                                    <p className="repo-empty-msg">Nenhuma atividade</p>
                                ) : atividade.map(a => (
                                    <div key={a.id} className="repo-act-item">
                                        <span className="repo-act-icon">{a.acao === 'Upload' ? '📄' : a.acao === 'Exclusão' ? '🗑️' : '📝'}</span>
                                        <div className="repo-act-info">
                                            <div className="repo-act-file">{a.documento_nome || a.descricao}</div>
                                            <div className="repo-act-meta">{a.acao} • {a.usuario}</div>
                                        </div>
                                        <span className="repo-act-time">{timeAgo(a.criado_em)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Storage */}
                        <div className="repo-sidebar-card repo-storage-card">
                            <div className="repo-storage-title">💾 Armazenamento</div>
                            <div className="repo-storage-bar">
                                <div className="repo-storage-fill" style={{ width: `${Math.min((stats.armazenamento_usado / (50*1024*1024*1024)) * 100, 100)}%` }}></div>
                            </div>
                            <div className="repo-storage-info">
                                <span className="repo-storage-used">{formatBytes(stats.armazenamento_usado)} usado</span>
                                <span>50 GB disponível</span>
                            </div>
                            {stats.por_extensao.length > 0 && (
                                <div className="repo-storage-types">
                                    {stats.por_extensao.map((t, i) => (
                                        <span key={i} className="repo-type-tag">{t.ext || '?'} ({t.total})</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Modal */}
            {uploadModal && renderUploadModal()}
        </div>
    );
}

export default Repositorio;
